import React from 'react';
import FreelancerSite from './FreelancerSite';
import ShopSite from './ShopSite';
import CreativeSite from './CreativeSite';
import { Box, Typography, Button } from '@mui/material';
import {
  ShoppingBagRounded, RestaurantRounded, BusinessCenterRounded, PaletteRounded,
  FavoriteRounded, SchoolRounded, FlightTakeoffRounded, CheckCircleRounded,
  FormatQuoteRounded, PlaceRounded, PhoneRounded, MailRounded,
} from '@mui/icons-material';

const CATEGORY_ICON = {
  Shop: ShoppingBagRounded,
  Food: RestaurantRounded,
  Business: BusinessCenterRounded,
  Creative: PaletteRounded,
  Services: SchoolRounded,
  'Beauty & Health': FavoriteRounded,
  'Blog & Magazine': FlightTakeoffRounded,
};

// Stand-in artwork: a soft gradient tile with the category icon, so templates
// ship without any third-party imagery.
const Artwork = ({ template, height, index = 0 }) => {
  const Icon = CATEGORY_ICON[template.category] || ShoppingBagRounded;
  const { primary, accent } = template.colors;
  const angle = 120 + index * 35;
  return (
    <Box sx={{
      height, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(${angle}deg, ${accent}33 0%, ${primary}22 100%)`,
      border: `1px solid ${primary}14`,
    }}>
      <Icon sx={{ fontSize: Math.min(72, height / 2.6), color: `${primary}88` }} />
    </Box>
  );
};

const Section = ({ id, children, sx }) => (
  <Box component="section" id={id} sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 9 }, scrollMarginTop: 60, ...sx }}>
    <Box sx={{ maxWidth: 1120, mx: 'auto' }}>{children}</Box>
  </Box>
);

const ITEMS_HEADING = {
  Shop: 'Best sellers',
  Food: 'On the menu',
  Business: 'Services & plans',
  Creative: 'What we offer',
  Services: 'Services & packages',
  'Beauty & Health': 'Plans & treatments',
  'Blog & Magazine': 'Latest stories',
};

// Renders a complete one-page website from a template definition. Used full
// size on /templates/:slug and scaled down as the gallery thumbnail.
const TemplateSite = ({ template, onAction, preview = false }) => {
  if (template.layout === 'freelancer') return <FreelancerSite template={template} onAction={onAction} preview={preview} />;
  if (template.layout === 'shop') return <ShopSite template={template} onAction={onAction} preview={preview} />;
  if (template.layout === 'creative') return <CreativeSite template={template} onAction={onAction} preview={preview} />;
  const { primary, accent, bg } = template.colors;
  const act = onAction || (() => {});
  // Thumbnails render many sites on one page, so they skip the anchor ids.
  const sid = (id) => (preview ? undefined : id);
  // Thumbnails sit inside a card link, so they render links and buttons as
  // plain spans to avoid nesting interactive elements.
  const action = (href) => (preview ? { component: 'span' } : href ? { href } : { onClick: act });
  const navLinks = [['Home', 'home'], [ITEMS_HEADING[template.category] || 'Shop', 'items'], ['About', 'about'], ['Contact', 'contact']];

  return (
    <Box sx={{ bgcolor: bg, color: primary, fontFamily: '"Poppins", "Inter", sans-serif', minWidth: 0 }}>
      {/* Navigation */}
      <Box sx={{
        px: { xs: 3, md: 8 }, py: 2.5, display: 'flex', alignItems: 'center', gap: 3,
        borderBottom: `1px solid ${primary}12`, bgcolor: bg,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: 2, bgcolor: accent, flexShrink: 0 }} />
          <Typography noWrap sx={{ fontWeight: 800, fontSize: 18 }}>{template.name}</Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3.5 }}>
          {navLinks.map(([label, id]) => (
            <Typography key={id} {...(preview ? { component: 'span' } : { component: 'a', href: `#${id}` })}
              sx={{ color: 'inherit', textDecoration: 'none', fontSize: 14, fontWeight: 500, opacity: 0.75, '&:hover': { opacity: 1 } }}>
              {label}
            </Typography>
          ))}
        </Box>
        <Button {...action()} size="small"
          sx={{ bgcolor: primary, color: '#fff', px: 2.5, '&:hover': { bgcolor: primary, opacity: 0.9 } }}>
          {template.category === 'Shop' ? 'Shop now' : 'Get in touch'}
        </Button>
      </Box>

      {/* Hero */}
      <Section id={sid('home')} sx={{ pt: { xs: 6, md: 10 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: { xs: 4, md: 6 }, alignItems: 'center' }}>
          <Box>
            <Typography sx={{ color: accent, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', fontSize: 13, mb: 2 }}>
              {template.tagline}
            </Typography>
            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 34, md: 52 }, lineHeight: 1.1, letterSpacing: '-0.02em', mb: 2.5 }}>
              {template.hero.title}
            </Typography>
            <Typography sx={{ fontSize: 17, opacity: 0.7, mb: 4, maxWidth: 480 }}>{template.hero.subtitle}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button {...action('#items')} sx={{ bgcolor: accent, color: '#fff', px: 3.5, py: 1.25, '&:hover': { bgcolor: accent, opacity: 0.9 } }}>
                Explore
              </Button>
              <Button {...action('#about')} sx={{ color: primary, border: `1.5px solid ${primary}33`, px: 3.5, py: 1.25 }}>
                Learn more
              </Button>
            </Box>
          </Box>
          <Artwork template={template} height={340} />
        </Box>
      </Section>

      {/* Highlights */}
      <Box sx={{ bgcolor: primary, color: '#fff', px: { xs: 3, md: 8 }, py: 3.5 }}>
        <Box sx={{ maxWidth: 1120, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          {template.features.map(f => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: { sm: 'center' } }}>
              <CheckCircleRounded sx={{ color: accent, fontSize: 22 }} />
              <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{f}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Products / services */}
      <Section id={sid('items')}>
        <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 26, md: 34 }, mb: 1 }}>
          {ITEMS_HEADING[template.category] || 'Featured'}
        </Typography>
        <Typography sx={{ opacity: 0.65, mb: 4 }}>{template.description}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          {template.items.map((item, i) => (
            <Box key={item.name} sx={{ bgcolor: '#fff', borderRadius: 4, p: 1.5, boxShadow: `0 6px 24px ${primary}10` }}>
              <Artwork template={template} height={170} index={i + 1} />
              <Box sx={{ px: 1, pt: 2, pb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15.5 }}>{item.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                  <Typography sx={{ color: accent, fontWeight: 700 }}>{item.price}</Typography>
                  <Button size="small" {...action()}
                    sx={{ minWidth: 0, px: 1.75, color: primary, border: `1px solid ${primary}22`, fontSize: 12.5 }}>
                    {template.category === 'Shop' ? 'Add' : 'Enquire'}
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>

      {/* About + testimonial */}
      <Section id={sid('about')} sx={{ bgcolor: '#fff' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 8 }, alignItems: 'center' }}>
          <Artwork template={template} height={300} index={5} />
          <Box>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 26, md: 34 }, mb: 2 }}>About {template.name}</Typography>
            <Typography sx={{ opacity: 0.7, mb: 3, lineHeight: 1.8 }}>
              We started with a simple idea: {template.tagline.toLowerCase()} Today we serve thousands of happy
              customers with the same care we had on day one. Replace this text with your own story.
            </Typography>
            <Box sx={{ bgcolor: bg, borderRadius: 4, p: 3, borderLeft: `4px solid ${accent}` }}>
              <FormatQuoteRounded sx={{ color: accent }} />
              <Typography sx={{ fontStyle: 'italic', opacity: 0.85, mb: 1.5 }}>
                “Exactly what we were looking for. Friendly team, great quality and quick service every time.”
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>A happy customer</Typography>
            </Box>
          </Box>
        </Box>
      </Section>

      {/* Contact */}
      <Section id={sid('contact')}>
        <Box sx={{
          borderRadius: 5, p: { xs: 4, md: 6 }, color: '#fff',
          background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 60%, ${accent} 160%)`,
          display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 4, alignItems: 'center',
        }}>
          <Box>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 26, md: 34 }, mb: 1.5 }}>Let’s talk</Typography>
            <Typography sx={{ opacity: 0.75, mb: 3 }}>Questions, bulk orders or bookings — we usually reply within a day.</Typography>
            <Button {...action()} sx={{ bgcolor: accent, color: '#fff', px: 3.5, py: 1.25, '&:hover': { bgcolor: accent, opacity: 0.9 } }}>
              Contact us
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[[PlaceRounded, 'Your street, your city'], [PhoneRounded, '+91 00000 00000'], [MailRounded, 'hello@yourbusiness.com']].map(([Icon, text]) => (
              <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Icon sx={{ color: accent }} />
                <Typography sx={{ opacity: 0.85 }}>{text}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Section>

      <Box sx={{ px: { xs: 3, md: 8 }, py: 3, borderTop: `1px solid ${primary}12`, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 13, opacity: 0.6 }}>© {new Date().getFullYear()} {template.name}. All rights reserved.</Typography>
      </Box>
    </Box>
  );
};

export default TemplateSite;
